/**
 * NotificationItem - Single notification row
 *
 * Used in both the dropdown and the notifications page.
 */

import type { NotificationsNotificationResponse } from '@smarthome/api';
import { formatRelativeTime } from '@smarthome/ui';
import { Bell } from 'lucide-react';

import { cn } from '@/components/ui/utils';

export interface NotificationItemProps {
  notification: NotificationsNotificationResponse;
  onClick?: () => void;
  /** Compact mode for dropdown */
  compact?: boolean;
  className?: string;
}

export function NotificationItem({
  notification,
  onClick,
  compact = false,
  className,
}: NotificationItemProps) {
  const isUnread = !notification.readAt;
  const title = notification.title || (notification.body ? notification.body.slice(0, 50) : '');
  const displayTitle = title.length > 50 ? `${title.slice(0, 47)}...` : title;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 text-left transition-colors',
        'hover:bg-gray-100 dark:hover:bg-white/[0.04]',
        compact ? 'px-4 py-3' : 'rounded-lg px-4 py-4',
        isUnread && 'bg-blue-50/50 dark:bg-blue-500/5',
        className
      )}
    >
      {/* Icon with unread indicator */}
      <div className="relative flex-shrink-0">
        <div
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-full',
            isUnread
              ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'
              : 'bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-zinc-400'
          )}
        >
          <Bell className="h-4 w-4" />
        </div>
        {isUnread && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-white dark:ring-zinc-900" />
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-sm',
            isUnread
              ? 'font-medium text-gray-900 dark:text-white'
              : 'text-gray-700 dark:text-zinc-300'
          )}
        >
          {displayTitle}
        </p>
        {!compact && notification.body && notification.title && (
          <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-400">
            {notification.body.length > 100
              ? `${notification.body.slice(0, 97)}...`
              : notification.body}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-400 dark:text-zinc-500">
          {notification.createdAt ? formatRelativeTime(notification.createdAt) : ''}
        </p>
      </div>
    </button>
  );
}
